import React, { useState, ReactNode } from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { BundleWork } from 'js/bulk_download/work_cards_shared/types';
import { WorkCard } from 'js/bulk_download/work_cards_shared/WorkCard';
import { PaperPreviewList } from 'js/bulk_download/work_cards_shared/PaperPreviewList';

interface Props {
  relatedWorks: BundleWork[];
  visibleRelatedWorks: BundleWork[];
  sourceWorkPreview: BundleWork | undefined;
  includedWorkSlot: ReactNode;
  showAnnotations: boolean;
  badgePlacement: 'side' | 'below' | 'above';
  previewFooter: ReactNode;
  emptyState: ReactNode;
  showBucketBadges: boolean;
  totalRelatedCount: number;
}

export function ProgressivePreviewList({
  visibleRelatedWorks,
  sourceWorkPreview,
  includedWorkSlot,
  showAnnotations,
  badgePlacement,
  previewFooter,
  emptyState,
  showBucketBadges,
  totalRelatedCount,
}: Props) {
  const [expanded, setExpanded] = useState(false);

  if (!expanded) {
    return (
      <CollapsedWrap>
        {sourceWorkPreview && (
          <WorkCard
            work={sourceWorkPreview}
            header="The paper you are reading (included)"
            showRelevancy={false}
            showAnnotations={showAnnotations}
            badgePlacement={badgePlacement}
          />
        )}
        <ExpandPrompt>
          <Body size="md" as="div" style={primaryText}>
            {totalRelatedCount >= 37
              ? '37+ related papers available'
              : `${totalRelatedCount} related papers available`}
          </Body>
          <Button size="sm" secondary onClick={() => setExpanded(true)}>
            See what&apos;s included
          </Button>
        </ExpandPrompt>
      </CollapsedWrap>
    );
  }

  return (
    <PaperPreviewList
      works={visibleRelatedWorks}
      includedWorkSlot={includedWorkSlot}
      showAnnotations={showAnnotations}
      badgePlacement={badgePlacement}
      showBucketBadges={showBucketBadges}
      maxHeight="100%"
      footer={previewFooter}
      emptyState={emptyState}
      style={{ flex: 1, minHeight: 0 }}
    />
  );
}

const primaryText: React.CSSProperties = {
  color: 'var(--text-primary, #141413)',
};

const CollapsedWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const ExpandPrompt = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs-16, 16px);
  padding: var(--spacing-sm-24, 24px);
  flex: 1;
`;
